import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { quotaAPI } from '../services/api';

const GenerateChoice = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [quota, setQuota] = useState(null);

  useEffect(() => {
    fetchQuota();
  }, []);

  const fetchQuota = async () => {
    try {
      const response = await quotaAPI.getQuota();
      setQuota(response.data);
    } catch (err) {
      console.error('Error fetching quota:', err);
    }
  };

  return (
    <div className="min-h-screen bg-[#0a0a1e]">
      {/* Header */}
      <header className="border-b border-gray-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-5 flex justify-between items-center">
          <div className="flex items-center space-x-2">
            <svg className="w-8 h-8 text-purple-500" fill="currentColor" viewBox="0 0 24 24">
              <path d="M14 2H6c-1.1 0-2 .9-2 2v16c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V8l-6-6zm-1 2l5 5h-5V4zM6 20V4h6v6h6v10H6z"/>
            </svg>
            <span className="text-xl font-semibold text-white">CV Pro</span>
          </div>
          <div className="flex items-center space-x-4">
            {quota && (
              <div className="flex items-center space-x-2">
                <span className="text-sm text-gray-400">CVs remaining:</span>
                <div className="flex items-center space-x-1">
                  <span className="text-white font-bold">{quota.remaining}</span>
                  <span className="text-gray-500">/</span>
                  <span className="text-gray-400">{quota.limit}</span>
                </div>
                <div className="h-2 w-20 bg-gray-800 rounded-full overflow-hidden">
                  <div 
                    className="h-full bg-gradient-to-r from-purple-500 to-pink-500 transition-all"
                    style={{ width: `${(quota.used / quota.limit) * 100}%` }}
                  />
                </div>
              </div>
            )}
            <span className="text-gray-400">{user?.email}</span>
            <button
              onClick={logout}
              className="text-gray-400 hover:text-white transition-colors"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
              </svg>
            </button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <div className="text-center mb-12">
          <h1 className="text-5xl font-bold text-white mb-4">
            Create Your{' '}
            <span className="bg-gradient-to-r from-purple-400 to-pink-400 bg-clip-text text-transparent">
              Professional CV
            </span>
          </h1>
          <p className="text-xl text-gray-400">
            Choose how you'd like to get started. Paste an existing CV or build one from scratch.
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-6">
          {/* Paste Existing CV */}
          <button
            onClick={() => navigate('/paste-cv')}
            className="group relative bg-gray-900 border border-gray-800 rounded-2xl p-8 text-left hover:border-purple-500 transition-all hover:shadow-xl hover:shadow-purple-500/20"
          >
            <div className="flex items-start justify-between mb-6">
              <div className="w-16 h-16 bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl flex items-center justify-center">
                <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
              </div>
            </div>
            <h3 className="text-2xl font-bold text-white mb-3">Paste Existing CV</h3>
            <p className="text-gray-400 mb-4">
              Have a CV already? Paste the text and let AI organize it into a professional format.
            </p>
            <div className="flex items-center text-purple-400 text-sm font-medium">
              <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"/>
              </svg>
              AI-Powered
            </div>
            <div className="absolute bottom-4 right-4 text-gray-600 group-hover:text-purple-400 transition-colors">
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
              </svg>
            </div>
          </button>

          {/* Start from Scratch */}
          <button
            onClick={() => navigate('/templates')}
            className="group relative bg-gray-900 border border-gray-800 rounded-2xl p-8 text-left hover:border-blue-500 transition-all hover:shadow-xl hover:shadow-blue-500/20"
          >
            <div className="flex items-start justify-between mb-6">
              <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-cyan-500 rounded-xl flex items-center justify-center">
                <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                </svg>
              </div>
            </div>
            <h3 className="text-2xl font-bold text-white mb-3">Start from Scratch</h3>
            <p className="text-gray-400 mb-4">
              Build your CV step by step with our guided form. Perfect for creating something new.
            </p>
            <div className="flex items-center text-cyan-400 text-sm font-medium">
              <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
              </svg>
              Get Started
            </div>
            <div className="absolute bottom-4 right-4 text-gray-600 group-hover:text-blue-400 transition-colors">
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
              </svg>
            </div>
          </button>
        </div>

        {/* Note */}
        <div className="mt-12 text-center">
          <p className="text-sm text-gray-500">
            Both options use AI to optimize your content for maximum impact
          </p>
        </div>
      </div>
    </div>
  );
};

export default GenerateChoice;
